import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useBooking } from "@/context/booking";
import { api } from "@/lib/api";
import { EVENT_TYPES } from "@/data";

const empty = { name: "", email: "", phone: "", event_type: "", event_date: "", guests: "", budget: "", details: "" };

export const BookingDialog = () => {
  const { open, setOpen, type } = useBooking();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (type) setForm((f) => ({ ...f, event_type: type })); }, [type, open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.event_type) {
      toast.error("Please fill in your name, email, phone and event type.");
      return;
    }
    setLoading(true);
    try {
      await api.booking(form);
      toast.success("Request sent! Epic Memories will reach out shortly.");
      setForm(empty); setOpen(false);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="booking-dialog" className="bg-[#0f0f0f] border border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Book Your <span className="gold-text italic">Epic</span> Event</DialogTitle>
          <DialogDescription className="text-zinc-400">Tell us about your vision — we&apos;ll craft the rest.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name *"><Input data-testid="booking-name" value={form.name} onChange={set("name")} className="bg-black/40 border-white/15" /></Field>
            <Field label="Phone *"><Input data-testid="booking-phone" value={form.phone} onChange={set("phone")} className="bg-black/40 border-white/15" /></Field>
          </div>
          <Field label="Email *"><Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} className="bg-black/40 border-white/15" /></Field>
          <Field label="Event Type *">
            <Select value={form.event_type} onValueChange={set("event_type")}>
              <SelectTrigger data-testid="booking-type" className="bg-black/40 border-white/15"><SelectValue placeholder="Choose an event" /></SelectTrigger>
              <SelectContent className="bg-[#141414] text-white border-white/10 max-h-64">
                {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Date"><Input data-testid="booking-date" type="date" value={form.event_date} onChange={set("event_date")} className="bg-black/40 border-white/15" /></Field>
            <Field label="Guests"><Input data-testid="booking-guests" value={form.guests} onChange={set("guests")} className="bg-black/40 border-white/15" /></Field>
            <Field label="Budget"><Input data-testid="booking-budget" value={form.budget} onChange={set("budget")} className="bg-black/40 border-white/15" placeholder="$" /></Field>
          </div>
          <Field label="Details"><Textarea data-testid="booking-details" value={form.details} onChange={set("details")} className="bg-black/40 border-white/15" rows={3} /></Field>
          <button data-testid="booking-submit" disabled={loading}
            className="w-full bg-[#D4AF37] text-black font-heading font-bold rounded-full py-3.5 hover:bg-[#F3E5AB] transition-colors disabled:opacity-60">
            {loading ? "Sending…" : "Send Booking Request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wide text-zinc-400">{label}</Label>
    {children}
  </div>
);
